import React from 'react';
import { MdUpload } from 'react-icons/md';

/**
 * Upload / processing banner for the Excel asset import. Rendered by both
 * AllAssets and AssetRegistration straight from the state `useAssetImport()`
 * hands back — see lib/useAssetImport.js.
 *
 * Why two visual states: the upload half is genuinely measurable (axios
 * `onUploadProgress` gives us loaded/total), but once the last byte is sent
 * the server still has to parse and validate the whole workbook and tells us
 * nothing until it answers. Freezing a determinate bar at 100% reads as
 * "finished, but the page is hung"; the indeterminate bar plus an explicit
 * "Processing…" label says the wait is expected.
 */
const ImportProgress = ({ phase, percent, fileName }) => {
  if (!phase) return null;

  const indeterminate = phase === 'processing' || percent == null;
  const label = phase === 'processing' ? 'Processing…' : 'Uploading…';
  const hint = phase === 'processing'
    ? 'File uploaded — the server is reading and validating every row. Please keep this page open.'
    : 'Sending the file to the server.';

  return (
    <div className="card" style={{ marginBottom: 12, padding: 14, borderLeft: '4px solid var(--accent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <MdUpload style={{ fontSize: 18, color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0 }}>
          {label}
          {fileName && (
            <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6, wordBreak: 'break-all' }}>
              {fileName}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
          {indeterminate ? '—' : `${percent}%`}
        </div>
      </div>

      <div
        className="progress-bar-wrap"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={indeterminate ? undefined : percent}
      >
        <div
          className={`progress-bar-fill${indeterminate ? ' progress-bar-indeterminate' : ''}`}
          style={indeterminate ? undefined : { width: `${percent}%`, background: 'var(--accent)' }}
        />
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{hint}</div>
    </div>
  );
};

export default ImportProgress;
